import { Guid } from "guid-typescript";

export class ApiHelper {

	public static parseResponseBody = async (response: Response, requireJson: boolean = false): Promise<any | any[]> => {

		// Get Content Type
		const contentType = response.headers.get("Content-Type");

		// Check for proper content type
		if (requireJson || (contentType && contentType.indexOf("application/json") !== -1)) {

			// Request JSON from response
			const json: string = await response.json();

			// Parse IDs
			ApiHelper.parseGuidStringsToObjects(json, "id");

			// Return JSON from response
			return json;
		}
		else {

			// Request body of response
			const body: string = await response.text();

			// Return text from response
			return body;
		}
	}

	public static parseResponseBlobAsDataUri = async (response: Response): Promise<string> => {

		return new Promise<string>(async (resolve, reject) => {

			// Initialize file reader
			const reader = new FileReader();

			// Configure file reader
			reader.onload = () => {

				// Resolve promise
				resolve(typeof (reader.result) === "string" ? reader.result : "");
			};

			reader.onabort = () => {

				// Reject promise
				reject();
			};

			// Get blob from response
			const blob = await response.blob();

			// Execute file reader
			reader.readAsDataURL(blob);
		});
	}

	public static parseResponseBlob = async (response: Response): Promise<Blob> => {

		// Get blob from response
		const blob = await response.blob();

		// Return blob
		return blob;
	}

	public static handleError = async (response: Response | string, onError?: (message: string) => void): Promise<void> => {

		// Initialize Error Message
		let errorMessage: string | undefined;

		// Check Response For Provided Message
		if (typeof (response) !== "string") {

			// Parse response body
			errorMessage = await ApiHelper.parseResponseBody(response);
		}
		else {

			// Store Message
			errorMessage = response;
		}

		// Check For Error Handler & Check Error Message
		if (onError && errorMessage && errorMessage !== "") {

			// Trigger Validation Error Handler
			onError(errorMessage);
		}
	}

	public static buildQueryString = (parameters: any): string => {

		// Check for parameters
		if (parameters) {

			// Initialize query string
			let queryString = "";

			// Loop through parameters
			for (const parameter in parameters) {

				// Check For Undefined
				if (parameter !== undefined && parameters[parameter] !== undefined) {

					// Search for value property
					const parameterValue = `${parameters[parameter]}`;

					// Check value property
					if (parameterValue !== undefined && parameterValue !== null && parameterValue !== "") {

						// Check for first parameter in query string
						if (queryString === "") {

							// Append ampersand
							queryString += "?";
						}
						else {

							// Append ampersand
							queryString += "&";
						}

						// Append parameter name
						queryString += parameter;

						// Append parameter value
						queryString += "=" + parameterValue;
					}
				}
			}

			return queryString;
		}

		// Return nothing
		return "";
	}

	public static parseDateString = (record: any, ...propertiesToParse: string[]): void => {

		// Check For Record
		if (record && record !== null) {

			// Check For Array
			if (Array.isArray(record)) {

				// Loop Through Items In Array
				for (const value of record) {

					// Parse Ids In Array Item
					ApiHelper.parseGuidStringsToObjects(value, ...propertiesToParse);
				}
			}
			else if (typeof record === "object") {

				// Loop Through Properties To Parse
				for (const propertyName of propertiesToParse) {

					// Check For Property & Ensure Property Is String & Ensure Property Is Valid Date and Time
					if (record[propertyName] && typeof record[propertyName] === "string" && !isNaN(Date.parse(record[propertyName] as string))) {

						// Create Date From Date and Time String
						const parsedDate = new Date(record[propertyName] as string);

						// Return Valid, Parsed Date (Without Time)
						record[propertyName] = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
					}
				}

				// Loop Through Each Other Property
				for (const propertyName in record) {

					// Ensure Property Is Directly On Record
					if (record.hasOwnProperty(propertyName)) {

						// Parse Property
						ApiHelper.parseGuidStringsToObjects(record[propertyName], ...propertiesToParse);
					}
				}
			}
		}
	}

	public static parseDateTimeString = (record: any, ...propertiesToParse: string[]): void => {

		// Check For Record
		if (record && record !== null) {

			// Check For Array
			if (Array.isArray(record)) {

				// Loop Through Items In Array
				for (const value of record) {

					// Parse Ids In Array Item
					ApiHelper.parseGuidStringsToObjects(value, ...propertiesToParse);
				}
			}
			else if (typeof record === "object") {

				// Loop Through Properties To Parse
				for (const propertyName of propertiesToParse) {

					// Check For Property & Ensure Property Is String & Ensure Property Is Valid Date and Time
					if (record[propertyName] && typeof record[propertyName] === "string" && !isNaN(Date.parse(record[propertyName] as string))) {

						// Return Valid, Parsed Date and Time
						record[propertyName] = new Date(record[propertyName] as string);
					}
				}

				// Loop Through Each Other Property
				for (const propertyName in record) {

					// Ensure Property Is Directly On Record
					if (record.hasOwnProperty(propertyName)) {

						// Parse Property
						ApiHelper.parseGuidStringsToObjects(record[propertyName], ...propertiesToParse);
					}
				}
			}
		}
	}

	public static parseGuidStringsToObjects = (record: any, ...propertiesToParse: string[]): void => {

		// Check For Record
		if (record && record !== null) {

			// Check For Array
			if (Array.isArray(record)) {

				// Loop Through Items In Array
				for (const value of record) {

					// Parse Ids In Array Item
					ApiHelper.parseGuidStringsToObjects(value, ...propertiesToParse);
				}
			}
			else if (typeof record === "object") {

				// Loop Through Properties To Parse
				for (const propertyName of propertiesToParse) {

					// Check For Property & Ensure Property Is String & Ensure Property Is Valid Guid
					if (record[propertyName] && typeof record[propertyName] === "string" && (record[propertyName] as string).match(Guid.validator)) {

						// Return Valid, Parsed Guid
						record[propertyName] = Guid.parse(record[propertyName] as string);
					}
				}

				// Loop Through Each Other Property
				for (const propertyName in record) {

					// Ensure Property Is Directly On Record
					if (record.hasOwnProperty(propertyName)) {

						// Parse Property
						ApiHelper.parseGuidStringsToObjects(record[propertyName], ...propertiesToParse);
					}
				}
			}
		}
	}
}
